// Monitoramento de infraestrutura (KAN-290, etapa 4).
//
// Le do backend do Portal TI, que por sua vez proxya o Prometheus interno.
// Polling de 10s no snapshot (padrao da tela de agentes). O detalhe do
// servidor abre num drawer lateral e faz UMA request extra pra serie
// temporal — evita puxar tudo o tempo todo.
//
// Sem dependencia de biblioteca de grafico: SVG inline pros donuts e pros
// mini-charts, mesmo padrao do Indicadores.js.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';

const POLL_MS = 10_000;

// Escala de cores. Verde -> amarelo -> vermelho seguem os thresholds das
// regras de alerta do Prometheus (>=75 warning, >=90 critical).
const COR = {
  ok:      '#4CB553',
  aviso:   '#E7A83A',
  critico: '#E05252',
  frio:    '#8FA5AC',
  down:    '#B94A4A',
  azul:    '#2A8A9C',
};

const STATUS_INFO = {
  ok:      { chip: 'Online',    icone: 'bi-check-circle-fill',  cor: COR.ok      },
  aviso:   { chip: 'Alerta',    icone: 'bi-exclamation-circle', cor: COR.aviso   },
  critico: { chip: 'Critico',   icone: 'bi-exclamation-octagon-fill', cor: COR.critico },
  frio:    { chip: 'Sem dados', icone: 'bi-question-circle',    cor: COR.frio    },
  down:    { chip: 'Offline',   icone: 'bi-x-circle-fill',      cor: COR.down    },
};

// A severidade "geral" do card e o pior entre os 3 recursos. Se `up===false`,
// vira Offline direto (nao adianta mostrar CPU velho de um host morto).
function severidadeGeral(s) {
  if (s.up === false) return 'down';
  const nums = [s.cpu, s.mem, s.disco].filter((v) => v !== null && v !== undefined);
  if (nums.length === 0) return 'frio';
  const max = Math.max(...nums);
  if (max >= 90) return 'critico';
  if (max >= 75) return 'aviso';
  return 'ok';
}

function corPorValor(v) {
  if (v === null || v === undefined) return COR.frio;
  if (v >= 90) return COR.critico;
  if (v >= 75) return COR.aviso;
  return COR.ok;
}

// --- Formatadores ------------------------------------------------------------
function formatarBytes(n) {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let i = 0; let v = Math.abs(n);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${u[i]}`;
}
function formatarBps(n) {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return `${formatarBytes(n)}/s`;
}
function formatarUptime(seg) {
  if (seg === null || seg === undefined || !Number.isFinite(seg) || seg < 0) return '—';
  const d = Math.floor(seg / 86400);
  const h = Math.floor((seg % 86400) / 3600);
  const m = Math.floor((seg % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
function formatarAgora(diffMs) {
  const s = Math.max(0, Math.floor(diffMs / 1000));
  if (s < 5) return 'agora';
  if (s < 60) return `há ${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `há ${m}min` : `há ${Math.floor(m / 60)}h`;
}

// --- Donut gauge (SVG) -------------------------------------------------------
// Circulo de fundo + arco colorido proporcional. Numero grande no centro,
// label embaixo. Cor vem por severidade. Sem valor: circulo cinza + "—".
function Donut({ label, valor, sufixo = '%', tamanho = 82 }) {
  const semDado = valor === null || valor === undefined;
  const pct = semDado ? 0 : Math.max(0, Math.min(100, valor));
  const cor = corPorValor(valor);
  const stroke = 8;
  const r = (tamanho - stroke) / 2;
  const c = 2 * Math.PI * r;
  const filled = (c * pct) / 100;
  const texto = semDado ? '—' : `${pct.toFixed(0)}`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flex: 1 }}>
      <svg width={tamanho} height={tamanho} viewBox={`0 0 ${tamanho} ${tamanho}`}>
        <circle
          cx={tamanho / 2} cy={tamanho / 2} r={r}
          fill="none" stroke="rgba(143,165,172,0.18)" strokeWidth={stroke}
        />
        {!semDado && (
          <circle
            cx={tamanho / 2} cy={tamanho / 2} r={r}
            fill="none" stroke={cor} strokeWidth={stroke}
            strokeDasharray={`${filled} ${c - filled}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${tamanho / 2} ${tamanho / 2})`}
            style={{ transition: 'stroke-dasharray .35s ease, stroke .2s' }}
          />
        )}
        <text
          x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
          fontSize={tamanho * 0.28} fontWeight="600" fill={semDado ? 'var(--portal-muted)' : 'var(--portal-text)'}
        >
          {texto}
        </text>
        {!semDado && (
          <text
            x="50%" y={tamanho / 2 + tamanho * 0.19} textAnchor="middle"
            fontSize={tamanho * 0.14} fill="var(--portal-muted)"
          >
            {sufixo}
          </text>
        )}
      </svg>
      <span style={{ fontSize: '.72rem', color: 'var(--portal-muted)', letterSpacing: '.02em', textTransform: 'uppercase' }}>
        {label}
      </span>
    </div>
  );
}

// --- Meta cell (grid 2x2) ----------------------------------------------------
function MetaCell({ icone, label, valor, cor }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <i className={`bi ${icone}`} style={{
        color: cor || 'var(--portal-muted)',
        fontSize: '.95rem', width: 16, textAlign: 'center', flexShrink: 0,
      }} />
      <div style={{ minWidth: 0, lineHeight: 1.15 }}>
        <div style={{ fontSize: '.65rem', color: 'var(--portal-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
          {label}
        </div>
        <div style={{
          fontSize: '.82rem', fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {valor === null || valor === undefined ? '—' : valor}
        </div>
      </div>
    </div>
  );
}

// --- Mini-chart de serie temporal (SVG inline, usado no drawer) --------------
function MiniChart({ pontos, cor, altura = 110 }) {
  const largura = 720;
  const padL = 34;
  const padR = 8;
  const padT = 10;
  const padB = 22;
  const innerW = largura - padL - padR;
  const innerH = altura - padT - padB;

  const validos = (pontos || []).filter(([, v]) => v !== null);
  if (validos.length === 0) {
    return (
      <div style={{ color: 'var(--portal-muted)', fontSize: '.85rem', padding: '.75rem 0', textAlign: 'center' }}>
        Sem dados no intervalo.
      </div>
    );
  }

  const ts = pontos.map(([t]) => t);
  const tMin = Math.min(...ts);
  const tMax = Math.max(...ts);
  const spanT = tMax - tMin || 1;
  const yMax = 100;

  const px = (t) => padL + ((t - tMin) / spanT) * innerW;
  const py = (v) => padT + innerH - (v / yMax) * innerH;

  // Path da linha + path fechado por baixo pra area preenchida (0 no eixo Y).
  let linha = '';
  let area = '';
  let dentro = false;
  pontos.forEach(([t, v], i) => {
    if (v === null) { dentro = false; return; }
    const cmd = dentro ? 'L' : 'M';
    linha += `${cmd} ${px(t).toFixed(1)} ${py(v).toFixed(1)} `;
    if (!dentro) area += `M ${px(t).toFixed(1)} ${py(0).toFixed(1)} L ${px(t).toFixed(1)} ${py(v).toFixed(1)} `;
    else area += `L ${px(t).toFixed(1)} ${py(v).toFixed(1)} `;
    if (i === pontos.length - 1 || pontos[i + 1]?.[1] === null) {
      area += `L ${px(t).toFixed(1)} ${py(0).toFixed(1)} Z `;
    }
    dentro = true;
  });

  const ticks = [0, 25, 50, 75, 100];
  const marcasX = 4;
  const marcasIdx = Array.from({ length: marcasX + 1 }, (_, i) => Math.floor((i * (pontos.length - 1)) / marcasX));
  const gradId = `g-${cor.replace('#', '')}`;

  return (
    <svg viewBox={`0 0 ${largura} ${altura}`} width="100%" height="auto" style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={cor} stopOpacity="0.28" />
          <stop offset="100%" stopColor={cor} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {ticks.map((t) => {
        const y = padT + innerH - (t / yMax) * innerH;
        return (
          <g key={t}>
            <line x1={padL} x2={largura - padR} y1={y} y2={y}
              stroke="rgba(143,165,172,0.12)" strokeDasharray="2 4" />
            <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="10" fill="var(--portal-muted)">
              {t}
            </text>
          </g>
        );
      })}
      <path d={area}  fill={`url(#${gradId})`} stroke="none" />
      <path d={linha} fill="none" stroke={cor} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
      {marcasIdx.map((i) => {
        const t = pontos[i]?.[0];
        if (t === undefined) return null;
        const rotulo = new Date(t * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        return (
          <text key={i} x={px(t)} y={altura - 6} textAnchor="middle" fontSize="10" fill="var(--portal-muted)">
            {rotulo}
          </text>
        );
      })}
    </svg>
  );
}

// --- Chip de status (com pulso quando online) --------------------------------
function ChipStatus({ severidade }) {
  const info = STATUS_INFO[severidade];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: '.72rem', fontWeight: 600, letterSpacing: '.02em',
      padding: '.3rem .6rem', borderRadius: 999,
      background: `${info.cor}18`, color: info.cor,
      border: `1px solid ${info.cor}44`,
    }}>
      <span
        className={severidade === 'ok' ? 'mon-pulse' : ''}
        style={{
          width: 8, height: 8, borderRadius: '50%', background: info.cor,
          boxShadow: severidade === 'ok' ? `0 0 0 0 ${info.cor}` : 'none',
        }}
      />
      {info.chip}
    </span>
  );
}

// --- Lista de discos (rodape do card) ---------------------------------------
// Host multi-volume: uma linha por disco/mountpoint com barrinha e
// tamanho. Se so tem um disco, degenera pra mesma info do rodape antigo
// (compact + centralizado).
function ListaDiscos({ discos }) {
  if (!discos || discos.length === 0) return null;
  const compacto = discos.length === 1;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 4,
      marginBottom: 10, fontSize: '.72rem', color: 'var(--portal-muted)',
    }}>
      {discos.map((d) => {
        const uso = d.usoPct;
        const cor = corPorValor(uso);
        const semDado = uso === null || uso === undefined;
        const pct = semDado ? 0 : Math.max(0, Math.min(100, uso));
        return (
          <div key={d.volume} style={{
            display: 'grid',
            gridTemplateColumns: compacto ? '1fr auto' : 'auto 1fr auto',
            alignItems: 'center', gap: 8,
          }}>
            {!compacto && (
              <span style={{
                fontWeight: 600, color: 'var(--portal-text)', letterSpacing: '.02em',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}>
                <i className="bi bi-hdd me-1" style={{ color: cor }} />
                {d.volume}
              </span>
            )}
            <div style={{
              height: 4, borderRadius: 3, background: 'rgba(143,165,172,0.18)',
              overflow: 'hidden', minWidth: 60,
            }}>
              {!semDado && (
                <div style={{
                  height: '100%', width: `${pct}%`, background: cor,
                  transition: 'width .35s ease, background .2s',
                }} />
              )}
            </div>
            <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {compacto && <i className="bi bi-hdd me-1" />}
              {formatarBytes(d.livreBytes)} livres / {formatarBytes(d.totalBytes)}
              {!semDado && <span style={{ color: cor, marginLeft: 6, fontWeight: 600 }}>{pct.toFixed(0)}%</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// --- Card ---------------------------------------------------------------------
function CardServidor({ dado, selecionado, onAbrir }) {
  const sev = severidadeGeral(dado);
  const info = STATUS_INFO[sev];
  const tempCor = dado.temperaturaC >= 75 ? COR.critico
                : dado.temperaturaC >= 60 ? COR.aviso
                : dado.temperaturaC !== null && dado.temperaturaC !== undefined ? COR.ok
                : 'var(--portal-muted)';

  return (
    <button
      type="button"
      onClick={onAbrir}
      style={{
        textAlign: 'left', width: '100%', font: 'inherit', color: 'inherit',
        border: `1px solid ${selecionado ? info.cor : 'var(--portal-border)'}`,
        borderRadius: 14, padding: '1rem 1.1rem', cursor: 'pointer',
        background: 'var(--portal-surface)',
        boxShadow: selecionado
          ? `0 0 0 3px ${info.cor}25, 0 8px 24px rgba(0,0,0,.35)`
          : '0 2px 6px rgba(0,0,0,.15)',
        transition: 'border-color .2s, box-shadow .25s, transform .15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontWeight: 600, fontSize: '1rem', color: 'var(--portal-text)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {dado.instance}
          </div>
          <div style={{
            fontSize: '.75rem', color: 'var(--portal-muted)', marginTop: 2,
            display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          }}>
            <span><i className="bi bi-hdd-network me-1" />{dado.ip || '—'}</span>
            {dado.so && (
              <span style={{
                padding: '.05rem .4rem', borderRadius: 4, fontSize: '.68rem',
                background: 'rgba(42,138,156,.15)', color: '#7BC5D3', textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}>{dado.so}</span>
            )}
            {dado.ambiente && (
              <span style={{
                padding: '.05rem .4rem', borderRadius: 4, fontSize: '.68rem',
                background: 'rgba(143,165,172,.15)', color: 'var(--portal-muted)',
              }}>{dado.ambiente}</span>
            )}
          </div>
        </div>
        <ChipStatus severidade={sev} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <Donut label="CPU"   valor={dado.cpu} />
        <Donut label="RAM"   valor={dado.mem} />
        <Donut label="Disco" valor={dado.disco} />
      </div>

      <ListaDiscos discos={dado.discos} />
      {(!dado.discos || dado.discos.length === 0) && dado.discoTotalBytes ? (
        <div style={{
          fontSize: '.72rem', color: 'var(--portal-muted)', textAlign: 'center', marginBottom: 10,
        }}>
          <i className="bi bi-hdd me-1" />
          {formatarBytes(dado.discoLivreBytes)} livres de {formatarBytes(dado.discoTotalBytes)}
        </div>
      ) : null}

      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem .9rem',
        paddingTop: 10, borderTop: '1px solid var(--portal-border)',
      }}>
        <MetaCell icone="bi-clock-history"   label="Uptime"      valor={formatarUptime(dado.uptimeSegundos)} />
        <MetaCell icone="bi-thermometer-half" label="Temperatura" cor={tempCor}
                  valor={dado.temperaturaC !== null && dado.temperaturaC !== undefined
                    ? `${dado.temperaturaC.toFixed(0)}°C` : '—'} />
        <MetaCell icone="bi-arrow-down-circle" label="Download" cor="#7BC5D3"
                  valor={formatarBps(dado.redeRxBps)} />
        <MetaCell icone="bi-arrow-up-circle"   label="Upload"   cor="#7BC5D3"
                  valor={formatarBps(dado.redeTxBps)} />
      </div>
    </button>
  );
}

// --- Drawer lateral com o detalhe -------------------------------------------
function DrawerDetalhe({ dado, detalhe, carregando, onFechar }) {
  useEffect(() => {
    if (!dado) return undefined;
    const key = (e) => { if (e.key === 'Escape') onFechar(); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [dado, onFechar]);

  const aberto = !!dado;
  return (
    <>
      {/* Overlay que escurece o resto quando o drawer esta aberto */}
      <div
        onClick={onFechar}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
          opacity: aberto ? 1 : 0, pointerEvents: aberto ? 'auto' : 'none',
          transition: 'opacity .2s', zIndex: 40,
        }}
      />
      <aside
        style={{
          position: 'fixed', top: 0, right: 0, height: '100vh', width: 520, maxWidth: '92vw',
          background: 'var(--portal-surface)', borderLeft: '1px solid var(--portal-border)',
          boxShadow: 'var(--portal-shadow)',
          transform: aberto ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform .28s cubic-bezier(.2,.9,.28,1)',
          zIndex: 41, display: 'flex', flexDirection: 'column',
        }}
      >
        {dado && (
          <>
            <div style={{
              padding: '1.1rem 1.25rem 1rem', borderBottom: '1px solid var(--portal-border)',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: 2 }}>
                  {dado.instance}
                </div>
                <div style={{ fontSize: '.78rem', color: 'var(--portal-muted)' }}>
                  {dado.ip}{dado.so ? ` · ${dado.so}` : ''}{dado.ambiente ? ` · ${dado.ambiente}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={onFechar}
                style={{
                  border: 'none', background: 'transparent', color: 'var(--portal-muted)',
                  fontSize: '1.2rem', cursor: 'pointer', padding: 4,
                }}
                title="Fechar (Esc)"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div style={{ padding: '1rem 1.25rem', overflowY: 'auto' }}>
              <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                <ChipStatus severidade={severidadeGeral(dado)} />
                {dado.uptimeSegundos !== null && dado.uptimeSegundos !== undefined && (
                  <span style={{ fontSize: '.75rem', color: 'var(--portal-muted)' }}>
                    <i className="bi bi-clock-history me-1" />
                    no ar há {formatarUptime(dado.uptimeSegundos)}
                  </span>
                )}
              </div>

              <div style={{ fontSize: '.72rem', color: 'var(--portal-muted)', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                Última hora · ponto a cada 60s
              </div>

              {carregando && (
                <div style={{ color: 'var(--portal-muted)', fontSize: '.85rem', padding: '1rem 0' }}>
                  Carregando série…
                </div>
              )}

              {!carregando && detalhe && (
                <>
                  <SecaoGrafico titulo="CPU"   cor={COR.azul}    pontos={detalhe.cpu} />
                  <SecaoGrafico titulo="RAM"   cor={COR.ok}      pontos={detalhe.mem} />
                  <SecaoGrafico titulo="Disco (pior volume)" cor={COR.aviso} pontos={detalhe.disco} />
                  {dado.discos && dado.discos.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <div style={{
                        fontSize: '.72rem', color: 'var(--portal-muted)',
                        marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em',
                      }}>
                        Volumes ({dado.discos.length})
                      </div>
                      <ListaDiscos discos={dado.discos} />
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
}

function SecaoGrafico({ titulo, cor, pontos }) {
  const ultimo = (pontos || []).slice().reverse().find(([, v]) => v !== null);
  const valorAtual = ultimo ? `${ultimo[1].toFixed(0)}%` : '—';
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--portal-text)' }}>{titulo}</span>
        <span style={{ fontSize: '.85rem', fontWeight: 600, color: cor }}>{valorAtual}</span>
      </div>
      <MiniChart pontos={pontos} cor={cor} />
    </div>
  );
}

// --- Alertname humanizado ----------------------------------------------------
// Prometheus usa CamelCase (ServidorForaDoAr). Quebra em "Sentence case"
// pra virar "Servidor fora do ar". Nomes ja com espaco passam intactos.
function humanizarAlertname(nome) {
  if (!nome) return '';
  if (nome.includes(' ')) return nome;
  return nome
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
}

// --- Painel de alertas -------------------------------------------------------
function PainelAlertas({ alertas }) {
  const firing = (alertas || []).filter((a) => a.state === 'firing');
  const hasFire = firing.length > 0;
  return (
    <div style={{
      border: `1px solid ${hasFire ? `${COR.critico}55` : 'var(--portal-border)'}`,
      borderRadius: 12, padding: '.85rem 1rem', marginBottom: 14,
      background: hasFire
        ? 'linear-gradient(135deg, rgba(224,82,82,.10), rgba(224,82,82,.03))'
        : 'var(--portal-surface)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: hasFire ? 10 : 0 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 8,
          display: 'grid', placeItems: 'center',
          background: hasFire ? `${COR.critico}22` : `${COR.ok}22`,
          color: hasFire ? COR.critico : COR.ok,
        }}>
          <i className={`bi ${hasFire ? 'bi-exclamation-triangle-fill' : 'bi-shield-check'}`}
             style={{ fontSize: '1.1rem' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '.92rem', fontWeight: 600 }}>
            {hasFire ? `${firing.length} alerta${firing.length > 1 ? 's' : ''} disparando agora` : 'Sem alertas ativos'}
          </div>
          <div style={{ fontSize: '.72rem', color: 'var(--portal-muted)', marginTop: 2 }}>
            {hasFire ? 'Verifique os hosts abaixo — os cards afetados aparecem com borda vermelha.' : 'Regras do Prometheus rodando normalmente.'}
          </div>
        </div>
      </div>
      {firing.map((a, idx) => (
        <div key={idx} style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '.55rem .7rem', borderRadius: 8, marginTop: 6,
          background: 'rgba(224,82,82,.08)', border: '1px solid rgba(224,82,82,.25)',
        }}>
          <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: COR.critico, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, fontSize: '.82rem' }}>
            <div style={{ fontWeight: 600 }}>{humanizarAlertname(a.labels?.alertname)}</div>
            <div style={{ color: 'var(--portal-muted)', marginTop: 2 }}>
              {a.labels?.instance || a.labels?.job}
              {a.labels?.severity ? ` · ${a.labels.severity}` : ''}
            </div>
            {a.annotations?.summary && (
              <div style={{ marginTop: 4 }}>{a.annotations.summary}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Chip de filtro por status (clicavel) -----------------------------------
function ChipFiltro({ ativo, cor, icone, label, valor, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: '.78rem', fontWeight: 500, letterSpacing: '.02em',
        padding: '.38rem .7rem', borderRadius: 999, cursor: 'pointer',
        background: ativo ? `${cor}25` : 'transparent',
        color: ativo ? cor : 'var(--portal-text)',
        border: `1px solid ${ativo ? `${cor}88` : 'var(--portal-border)'}`,
        transition: 'all .15s',
      }}
    >
      <i className={`bi ${icone}`} style={{ color: cor, fontSize: '.85rem' }} />
      {label}
      <span style={{
        fontWeight: 600, fontSize: '.72rem', padding: '.05rem .38rem', borderRadius: 6,
        background: ativo ? `${cor}30` : 'var(--portal-surface-soft)',
      }}>{valor}</span>
    </button>
  );
}

// --- Pagina -----------------------------------------------------------------
export default function Monitoramento() {
  const [servidores, setServidores] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [carregado, setCarregado] = useState(false);
  const [erro, setErro] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detalhes, setDetalhes] = useState({});
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [agora, setAgora] = useState(Date.now());
  const [busca, setBusca] = useState('');
  const [filtroSo, setFiltroSo] = useState('');
  const [filtroAmbiente, setFiltroAmbiente] = useState('');
  const [filtroStatus, setFiltroStatus] = useState(''); // ''|'ok'|'aviso'|'critico'|'down'|'frio'
  const timerRef = useRef(null);
  const abertoRef = useRef(null);
  useEffect(() => { abertoRef.current = selectedId; }, [selectedId]);

  const puxarSnapshot = useCallback(async () => {
    try {
      const [snap, ale] = await Promise.all([
        axios.get(`${API}/infra/servidores`),
        axios.get(`${API}/infra/alertas`),
      ]);
      setServidores(snap.data?.data || []);
      setAlertas(ale.data?.data || []);
      setUltimaAtualizacao(Date.now());
      setErro('');
    } catch (e) {
      setErro(e.response?.data?.message || 'Falha ao consultar o Prometheus.');
    } finally {
      setCarregado(true);
    }
  }, []);

  useEffect(() => {
    puxarSnapshot();
    timerRef.current = setInterval(puxarSnapshot, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [puxarSnapshot]);

  // Ticker de 1s pra o "há Xs" ir atualizando entre polls, sem refazer request.
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Detalhe sob demanda ao abrir card, com cache por instance.
  useEffect(() => {
    if (!selectedId) return undefined;
    if (detalhes[selectedId]) return undefined;
    let ativo = true;
    setCarregandoDetalhe(selectedId);
    axios.get(`${API}/infra/servidores/${encodeURIComponent(selectedId)}/metricas`)
      .then((res) => {
        if (!ativo || abertoRef.current !== selectedId) return;
        setDetalhes((prev) => ({ ...prev, [selectedId]: res.data?.data }));
      })
      .catch(() => { /* silencioso — o drawer mostra "sem dados" */ })
      .finally(() => { if (ativo) setCarregandoDetalhe(null); });
    return () => { ativo = false; };
  }, [selectedId, detalhes]);

  // Contagens por severidade (usadas nos chips de filtro).
  const contagem = useMemo(() => {
    const c = { total: 0, ok: 0, aviso: 0, critico: 0, down: 0, frio: 0 };
    servidores.forEach((s) => {
      c.total += 1;
      c[severidadeGeral(s)] += 1;
    });
    return c;
  }, [servidores]);

  // Listas de opcoes dos selects (calculado do payload atual).
  const opcoesSo = useMemo(
    () => Array.from(new Set(servidores.map((s) => s.so).filter(Boolean))).sort(),
    [servidores],
  );
  const opcoesAmbiente = useMemo(
    () => Array.from(new Set(servidores.map((s) => s.ambiente).filter(Boolean))).sort(),
    [servidores],
  );

  // Filtragem combinada. Filtro vazio = passa tudo.
  // Sort estavel por nome (localeCompare respeita acento e cadastro em pt-BR).
  // O backend ja devolve ordenado, mas mantemos aqui pra o card nao "pular"
  // se a fonte mudar no futuro (outro payload, cache, etc).
  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const filtro = servidores.filter((s) => {
      if (filtroSo && s.so !== filtroSo) return false;
      if (filtroAmbiente && s.ambiente !== filtroAmbiente) return false;
      if (filtroStatus && severidadeGeral(s) !== filtroStatus) return false;
      if (q) {
        const hay = `${s.instance || ''} ${s.ip || ''} ${s.ambiente || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return filtro.slice().sort((a, b) => (a.instance || '').localeCompare(b.instance || '', 'pt-BR'));
  }, [servidores, busca, filtroSo, filtroAmbiente, filtroStatus]);

  const selecionado = useMemo(
    () => servidores.find((s) => s.instance === selectedId) || null,
    [servidores, selectedId],
  );

  const toggleFiltroStatus = (chave) => setFiltroStatus((cur) => (cur === chave ? '' : chave));

  return (
    <>
      {/* Keyframe do pulso do chip "Online" — inline pra nao poluir o index.css */}
      <style>{`
        @keyframes mon-pulse {
          0%   { box-shadow: 0 0 0 0    ${COR.ok}80; }
          70%  { box-shadow: 0 0 0 8px  ${COR.ok}00; }
          100% { box-shadow: 0 0 0 0    ${COR.ok}00; }
        }
        .mon-pulse { animation: mon-pulse 1.8s ease-out infinite; }
        .mon-input {
          background: var(--portal-surface-soft);
          border: 1px solid var(--portal-border);
          color: var(--portal-text);
          border-radius: 8px;
          padding: .48rem .7rem;
          font-size: .85rem;
          outline: none;
          transition: border-color .15s;
        }
        .mon-input:focus { border-color: ${COR.azul}; }
        .mon-input::placeholder { color: var(--portal-muted); }
      `}</style>

      <div style={{ padding: '1.25rem 1.5rem', maxWidth: 1500, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, display: 'grid', placeItems: 'center',
              background: `${COR.azul}20`, color: COR.azul,
            }}>
              <i className="bi bi-activity" style={{ fontSize: '1.15rem' }} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>Monitoramento de infraestrutura</h2>
              <div style={{ fontSize: '.72rem', color: 'var(--portal-muted)', marginTop: 2 }}>
                Snapshot ao vivo · atualiza a cada 10s
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.78rem', color: 'var(--portal-muted)' }}>
            {erro ? (
              <span style={{ color: COR.critico }}>
                <i className="bi bi-exclamation-triangle-fill me-1" />
                falha ao consultar
              </span>
            ) : ultimaAtualizacao ? (
              <>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%', background: COR.ok, display: 'inline-block',
                }} className="mon-pulse" />
                <span>atualizado {formatarAgora(agora - ultimaAtualizacao)}</span>
              </>
            ) : (
              <span>carregando…</span>
            )}
            <button
              type="button"
              onClick={puxarSnapshot}
              className="mon-input"
              style={{ padding: '.35rem .6rem', cursor: 'pointer' }}
              title="Atualizar agora"
            >
              <i className="bi bi-arrow-clockwise" />
            </button>
          </div>
        </div>

        {erro && (
          <div style={{
            background: 'rgba(224,82,82,0.08)', border: '1px solid rgba(224,82,82,0.3)',
            borderRadius: 10, padding: '.7rem .9rem', marginBottom: 12, fontSize: '.85rem',
          }}>
            <i className="bi bi-exclamation-triangle-fill me-2" style={{ color: COR.critico }} />
            {erro}
          </div>
        )}

        <PainelAlertas alertas={alertas} />

        {/* Barra de filtros: chips por severidade + busca + selects */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 14,
        }}>
          <ChipFiltro
            ativo={filtroStatus === ''}
            cor="var(--portal-text)"
            icone="bi-grid-3x3-gap-fill"
            label="Todos"
            valor={contagem.total}
            onClick={() => setFiltroStatus('')}
          />
          <ChipFiltro
            ativo={filtroStatus === 'ok'}      cor={COR.ok}      icone="bi-check-circle"
            label="Online"    valor={contagem.ok}      onClick={() => toggleFiltroStatus('ok')}
          />
          {contagem.aviso > 0 && (
            <ChipFiltro
              ativo={filtroStatus === 'aviso'}   cor={COR.aviso}   icone="bi-exclamation-circle"
              label="Alerta"    valor={contagem.aviso}   onClick={() => toggleFiltroStatus('aviso')}
            />
          )}
          {contagem.critico > 0 && (
            <ChipFiltro
              ativo={filtroStatus === 'critico'} cor={COR.critico} icone="bi-exclamation-octagon"
              label="Critico"   valor={contagem.critico} onClick={() => toggleFiltroStatus('critico')}
            />
          )}
          {contagem.down > 0 && (
            <ChipFiltro
              ativo={filtroStatus === 'down'}    cor={COR.down}    icone="bi-x-circle"
              label="Offline"   valor={contagem.down}    onClick={() => toggleFiltroStatus('down')}
            />
          )}
          {contagem.frio > 0 && (
            <ChipFiltro
              ativo={filtroStatus === 'frio'}    cor={COR.frio}    icone="bi-question-circle"
              label="Sem dados" valor={contagem.frio}    onClick={() => toggleFiltroStatus('frio')}
            />
          )}

          <div style={{ flex: 1 }} />

          <div style={{ position: 'relative' }}>
            <i className="bi bi-search" style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--portal-muted)', fontSize: '.85rem',
            }} />
            <input
              type="text"
              className="mon-input"
              placeholder="Buscar nome, IP ou ambiente…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              style={{ paddingLeft: 30, width: 240 }}
            />
          </div>
          {opcoesSo.length > 1 && (
            <select className="mon-input" value={filtroSo} onChange={(e) => setFiltroSo(e.target.value)}>
              <option value="">Todos SOs</option>
              {opcoesSo.map((so) => <option key={so} value={so}>{so}</option>)}
            </select>
          )}
          {opcoesAmbiente.length > 1 && (
            <select className="mon-input" value={filtroAmbiente} onChange={(e) => setFiltroAmbiente(e.target.value)}>
              <option value="">Todos ambientes</option>
              {opcoesAmbiente.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          )}
        </div>

        {/* Estados vazios */}
        {!carregado && (
          <div style={{ color: 'var(--portal-muted)', fontSize: '.9rem', padding: '2rem 0', textAlign: 'center' }}>
            <i className="bi bi-hourglass-split me-2" />Carregando…
          </div>
        )}
        {carregado && servidores.length === 0 && (
          <div style={{
            border: '1px dashed var(--portal-border)', borderRadius: 12,
            padding: '2rem', textAlign: 'center', color: 'var(--portal-muted)',
          }}>
            <i className="bi bi-hdd-stack" style={{ fontSize: '1.5rem', display: 'block', marginBottom: 8 }} />
            Nenhum servidor com categoria "servidor" + SO + IP cadastrado em{' '}
            <a href="/ti/topologia">/ti/topologia</a>.
          </div>
        )}
        {carregado && servidores.length > 0 && filtrados.length === 0 && (
          <div style={{
            border: '1px dashed var(--portal-border)', borderRadius: 12,
            padding: '1.5rem', textAlign: 'center', color: 'var(--portal-muted)', fontSize: '.9rem',
          }}>
            Nenhum servidor bate com o filtro atual.
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 14,
        }}>
          {filtrados.map((s) => (
            <CardServidor
              key={s.instance}
              dado={s}
              selecionado={selectedId === s.instance}
              onAbrir={() => setSelectedId(s.instance)}
            />
          ))}
        </div>
      </div>

      <DrawerDetalhe
        dado={selecionado}
        detalhe={detalhes[selectedId]}
        carregando={carregandoDetalhe === selectedId && !detalhes[selectedId]}
        onFechar={() => setSelectedId(null)}
      />
    </>
  );
}
